import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import { injectStripe } from 'react-stripe-elements';
import { Box, Button, SubHeading, Text } from '@components/common';
import {
  StripeExpiryField,
  StripeCVCField,
  StripeCardField
} from '@components/form';
import AuthActions, { AuthSelectors } from '@redux/AuthRedux';
import API from '@utils/api';
import { StripeConfig } from '@utils/config';
import validationSchema from './schema';
import logger from '@utils/logger';
import { getErrors } from '@utils/error';
import { trackPayInfoChanged } from '../../../services/analytics';

class PaymentForm extends Component {
  handleSubmit = async (values, actions) => {
    const { setSubmitting, setStatus } = actions;
    const { stripe, user, setUser, onSubmit } = this.props;
    setSubmitting(true);

    let sourceId = '';
    try {
      const { source } = await stripe.createSource({
        type: 'card',
        owner: {
          name: `${user.firstName} ${user.lastName}`
        }
      });

      sourceId = source.id;
    } catch (err) {
      logger.log(err.message);
      setStatus({
        message: err.message
      });
      setSubmitting(false);
      return;
    }

    if (sourceId) {
      try {
        const { result } = await API.addPayment({
          token: StripeConfig.isProduction ? sourceId : 'tok_visa',
          isApplePay: false,
          isGooglePay: false
        });

        setUser(result);
        trackPayInfoChanged();
        onSubmit(result);
      } catch (err) {
        const error = getErrors(err);
        if (error.message) {
          setStatus({
            message: err.message
          });
        } else {
          setStatus({
            message:
              Object.values(error).pop() ||
              'There was an error in updating payment info'
          });
        }
        setSubmitting(false);
        return;
      }
    }

    setSubmitting(false);
  };

  render() {
    const { onCancel } = this.props;
    return (
      <Formik
        initialValues={{
          cardNumValid: false,
          cardExpValid: false,
          cardCVCValid: false
        }}
        onSubmit={this.handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isValid, isSubmitting, status }) => {
          return (
            <Form>
              <SubHeading textAlign="center" fontWeight="bold" mt={4} mb={4}>
                Payment Information
              </SubHeading>
              <Field component={StripeCardField} mb={4} name="cardNumValid" />
              <Box
                flexDirection="row"
                display="flex"
                justifyContent="space-between"
              >
                <Field
                  component={StripeExpiryField}
                  mr={4}
                  mb={4}
                  name="cardExpValid"
                />
                <Field component={StripeCVCField} mb={4} name="cardCVCValid" />
              </Box>

              {status && status.message && (
                <Text color="red" mb={4}>
                  {status.message}
                </Text>
              )}

              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={4}
              >
                <Button
                  type="button"
                  onClick={onCancel}
                  variant="secondary"
                  mr={4}
                  p={3}
                  flex={1}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  p={3}
                  flex={1}
                  disabled={!isValid || isSubmitting}
                >
                  Confirm
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

PaymentForm.propTypes = {
  stripe: PropTypes.any,
  user: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  setUser: PropTypes.func
};

const mapStatesToProps = state => ({
  user: AuthSelectors.selectCurrentUser(state)
});

const mapDispatchToProps = dispatch => ({
  setUser: user => dispatch(AuthActions.setUser(user))
});

const connectedForm = connect(
  mapStatesToProps,
  mapDispatchToProps
)(PaymentForm);

export default injectStripe(connectedForm);
